/**
 * Utilidad por producto y por cliente.
 *
 * Built from the sales themselves rather than from the ledger, because the ledger knows what
 * was sold and what it cost in total but not which product or which customer it belonged to.
 *
 * Cost comes from `costoPosteado` — what the reducer actually booked, at the average in effect
 * when the line sold. Re-deriving it from today's average would quietly rewrite last March's
 * margin every time a supplier changes their price.
 *
 * Revenue in BOTH views is line subtotal less the ticket discount, spread across the lines in
 * proportion to their value. Two consequences, both wanted: a discount given at the till lands
 * on the products it was given on rather than vanishing, and the two views reconcile to each
 * other and to `Ventas − Descuentos − Costo de ventas` in the ledger. Cash rounding is left out
 * of both — half a córdoba is an artefact of the coins that circulate, not customer revenue — and
 * so is what a customer paid for delivery, which is the trip's, not the goods' («Cobros por envío»).
 *
 * What came back through a return is taken off both, worked out the way the return itself
 * worked it out (`valorDevuelto`): a bottle that was sold and then returned was never a sale.
 */

import {
  ZERO,
  sumarTotales,
  centavos,
  repartirProporcional,
  saleToBase,
  subMoney,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import type { Venta } from '../state.js';
import { valorDevuelto } from '../projections/devoluciones.js';
import { enRango, type Rango } from './estados.js';

/** What the returns on one line of a sale took back: its quantity, what it brought in, what it cost. */
function devueltoDeLinea(v: Venta, i: number) {
  const devuelta = v.lineas[i]?.devuelta ?? 0;
  if (devuelta <= 0) return { cantidad: 0, neto: ZERO, costo: ZERO };
  const r = valorDevuelto(v, i, 0 as Milli, devuelta as Milli);
  return { cantidad: devuelta, neto: r.neto, costo: r.costo };
}

export interface FilaUtilidad {
  clave: string;
  nombre: string;
  /**
   * What moved, in the product's own unit: two cases of 24 and five loose are 53, not 7, which
   * added up presentations as if they were one (2026-10-13). Only meaningful per product, hence
   * null per client.
   */
  cantidad: Milli | null;
  ingreso: Centavos;
  costo: Centavos;
  utilidad: Centavos;
  /** Gross margin over revenue, in basis points. Null when nothing was sold. */
  margenBp: number | null;
  ventas: number;
}

function fila(clave: string, nombre: string): FilaUtilidad & { cantidad: Milli } {
  return {
    clave,
    nombre,
    cantidad: 0 as Milli,
    ingreso: ZERO,
    costo: ZERO,
    utilidad: ZERO,
    margenBp: null,
    ventas: 0,
  };
}

function cerrar(f: FilaUtilidad): FilaUtilidad {
  f.utilidad = sumarTotales(f.ingreso, -f.costo);
  f.margenBp = f.ingreso > 0 ? Math.round((f.utilidad * 10_000) / f.ingreso) : null;
  return f;
}

const vigentes = (ventas: readonly Venta[], rango: Rango): Venta[] =>
  ventas.filter((v) => !v.anulada && enRango(v.fecha, rango));

/**
 * One row per product, sorted by profit.
 *
 * Lines from a venta rápida — something sold before it existed in the catalog — are grouped
 * under their own row rather than dropped. They carry no cost, so showing them apart is what
 * stops a 100% margin from flattering the products that do have one.
 */
export function utilidadPorProducto(
  ventas: readonly Venta[],
  nombreDe: (productoId: string) => string | undefined,
  rango: Rango = {},
): FilaUtilidad[] {
  const out = new Map<string, FilaUtilidad & { cantidad: Milli }>();

  for (const v of vigentes(ventas, rango)) {
    const netos = netosDeLineas(v);
    const tocados = new Set<string>();
    for (const [i, l] of v.lineas.entries()) {
      const clave = l.productoId ?? 'sin-catalogo';
      const nombre = l.productoId
        ? (nombreDe(l.productoId) ?? l.descripcion)
        : 'Ventas rápidas (fuera de catálogo)';

      const f = out.get(clave) ?? fila(clave, nombre);
      const neto = netos[i]!;
      f.nombre = nombre;
      f.cantidad = (f.cantidad + saleToBase(neto.cantidad, l.factor)) as Milli;
      f.ingreso = sumarTotales(f.ingreso, neto.ingreso);
      f.costo = sumarTotales(f.costo, neto.costo);
      if (!tocados.has(clave)) {
        f.ventas += 1;
        tocados.add(clave);
      }
      out.set(clave, f);
    }
  }

  return [...out.values()].map(cerrar).sort((a, b) => b.utilidad - a.utilidad);
}

/**
 * Each line of a sale as profit by product counts it: what it brought in, the ticket's discount
 * spread over the lines by their value, what it cost, and the sale units it moved, what came back
 * taken off all three. What «Análisis» adds up by category and by price list too, so every view of
 * a sale's lines agrees with this one.
 */
export function netosDeLineas(v: Venta): { ingreso: Centavos; costo: Centavos; cantidad: Milli }[] {
  // Largest-remainder, so the allocated parts sum to exactly the discount that was given.
  const descuentos = repartirProporcional(
    v.descuento,
    v.lineas.map((l) => l.subtotal),
  );
  return v.lineas.map((l, i) => {
    const devuelto = devueltoDeLinea(v, i);
    return {
      ingreso: subMoney(subMoney(l.subtotal, descuentos[i]!), devuelto.neto),
      costo: subMoney(l.costoPosteado, devuelto.costo),
      cantidad: (l.cantidad - devuelto.cantidad) as Milli,
    };
  });
}

/**
 * What one sale brought in and what it cost, what came back taken off, before cash rounding and
 * delivery: what the per-client view adds up, and «Análisis» day by day.
 */
export function netoDeVenta(v: Venta): { ingreso: Centavos; costo: Centavos } {
  const devuelto = v.lineas.map((_, i) => devueltoDeLinea(v, i));
  return {
    ingreso: centavos(v.total - v.redondeo - (v.envio ?? 0) - devuelto.reduce((a, d) => a + d.neto, 0)),
    costo: centavos(v.costoTotal - devuelto.reduce((a, d) => a + d.costo, 0)),
  };
}

/**
 * What one sale left, as the books take it (2026-10-20): what it brought in net of its discount and
 * of what came back, with its delivery and what the dollars it took in are worth over the shop's
 * rate, less what it cost net of what came back. The sale's screen took `total − costoTotal`: the
 * coins' rounding in, which is no profit, what came back left in, and the exchange out.
 */
export function utilidadDeVenta(v: Venta): Centavos {
  const { ingreso, costo } = netoDeVenta(v);
  const cambiaria = v.pagos.reduce((a, p) => a + p.montoContable - p.monto, 0);
  return centavos(ingreso + (v.envio ?? 0) + cambiaria - costo);
}

/**
 * One row per client, sorted by profit.
 *
 * Counter sales with nobody attached are grouped together. The point of this report is finding
 * the wholesale customer who negotiated a price that no longer covers the cost, so a discount
 * given at the ticket level is charged against the client who received it.
 */
export function utilidadPorCliente(
  ventas: readonly Venta[],
  nombreDe: (clienteId: string) => string | undefined,
  rango: Rango = {},
): FilaUtilidad[] {
  const out = new Map<string, FilaUtilidad>();

  for (const v of vigentes(ventas, rango)) {
    const clave = v.clienteId ?? 'mostrador';
    const nombre = v.clienteId
      ? (nombreDe(v.clienteId) ?? 'Cliente eliminado')
      : 'Mostrador (sin cliente)';

    const f = out.get(clave) ?? { ...fila(clave, nombre), cantidad: null };
    f.nombre = nombre;
    // Net of the discount but before cash rounding and delivery, matching the per-product view exactly.
    const neto = netoDeVenta(v);
    f.ingreso = sumarTotales(f.ingreso, neto.ingreso);
    f.costo = sumarTotales(f.costo, neto.costo);
    f.ventas += 1;
    out.set(clave, f);
  }

  return [...out.values()].map(cerrar).sort((a, b) => b.utilidad - a.utilidad);
}

/** Totals for the header of either report, so the parts can be checked against the whole. */
export function totalUtilidad(filas: readonly FilaUtilidad[]): {
  ingreso: Centavos;
  costo: Centavos;
  utilidad: Centavos;
  margenBp: number | null;
} {
  const ingreso = filas.reduce<Centavos>((a, f) => sumarTotales(a, f.ingreso), ZERO);
  const costo = filas.reduce<Centavos>((a, f) => sumarTotales(a, f.costo), ZERO);
  const utilidad = sumarTotales(ingreso, -costo);
  return {
    ingreso,
    costo,
    utilidad,
    margenBp: ingreso > 0 ? Math.round((utilidad * 10_000) / ingreso) : null,
  };
}
